import React from 'react';
import {
  Typography,
  LinearProgress,
  Box,
  IconButton,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddNewButton from './components/AddNewButton';
import { useParams } from 'react-router';
import { usePackagerUpdate } from '../../hooks/packager/usePackagerUpdate';
import { packagerFormDirtyAtom } from '../../../atoms/packager-atoms';
import { useAtom } from 'jotai';
import DialogContentGlynt from '../../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../../common/Dialog/DialogActionsGlynt';
import { FormControlStyled, FormLabelStyled } from '../../common/Dialog/DialogGlynt';
import useGetPackager from '../../hooks/packager/useGetPackager';
import PackagerConfigUnsavedChanges from './PackagerConfigUnsavedChanges';
import TemplateEditorDialog from './TemplateEditorDialog';
import {
  Packager,
  CustomOutputConfig,
  OutputTemplate,
} from '../../../types/packager-types';

interface PackagerStep3TemplatesProps {
  updatePackagerMutation: ReturnType<typeof usePackagerUpdate>;
  handleCloseConfirmation: () => void;
  handlePrevious: () => void;
  handleNext: (skipGuard?: boolean) => void;
  simulateMode?: boolean;
  draftChanges?: Partial<Packager>;
  setDraftChanges?: (changes: Partial<Packager>) => void;
  triggerSimulation?: () => void;
  simulateLoading?: boolean;
}

const PackagerStep3Templates: React.FC<PackagerStep3TemplatesProps> = ({
  updatePackagerMutation,
  handleCloseConfirmation,
  handlePrevious,
  handleNext,
  simulateMode = false,
  draftChanges = {},
  setDraftChanges,
  triggerSimulation,
  simulateLoading = false,
}) => {
  const { dpid } = useParams<{ dpid: string }>();

  if (!dpid) {
    return (
      <DialogContentGlynt paddingTop="0">
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="error">
            Error: Missing Data Pool ID
          </Typography>
        </Box>
      </DialogContentGlynt>
    );
  }

  const { packager, isLoading } = useGetPackager(dpid);
  const [showUnsavedDialog, setShowUnsavedDialog] = React.useState(false);
  const [pendingCloseAction, setPendingCloseAction] = React.useState<(() => void) | null>(null);
  const [formDirty, setFormDirty] = useAtom(packagerFormDirtyAtom);

  const [templates, setTemplates] = React.useState<Record<string, OutputTemplate>>({});
  const [templateEditorOpen, setTemplateEditorOpen] = React.useState(false);
  const [editingTemplate, setEditingTemplate] = React.useState<{
    name: string;
    template: OutputTemplate;
  } | null>(null);
  const [templatesModified, setTemplatesModified] = React.useState(false);

  React.useEffect(() => {
    if (packager) {
      setTemplates(packager.custom_output_config?.templates || {});
      setTemplatesModified(false);
    }
  }, [packager]);

  React.useEffect(() => {
    if (!simulateMode) return;
    if (draftChanges.custom_output_config !== undefined) {
      setTemplates(draftChanges.custom_output_config?.templates || {});
    }
  }, [simulateMode, draftChanges]);

  React.useEffect(() => {
    setFormDirty(templatesModified);
  }, [templatesModified, setFormDirty]);

  const buildCustomOutputConfig = (): CustomOutputConfig | null => {
    if (Object.keys(templates).length === 0) return null;
    return { templates };
  };

  const persist = () => {
    if (!packager?.id) return;

    const customOutputConfig = buildCustomOutputConfig();
    const requestData: Partial<Packager> = {};
    if (customOutputConfig) {
      requestData.custom_output_config = customOutputConfig;
    } else {
      requestData.custom_output_config = null;
    }

    if (simulateMode && setDraftChanges) {
      setDraftChanges({ ...draftChanges, ...requestData });
      setFormDirty(false);
      setTemplatesModified(false);
      if (triggerSimulation) triggerSimulation();
    } else {
      updatePackagerMutation.mutate(
        { dataPoolId: dpid!, packagerId: packager.id, data: requestData },
        {
          onSuccess: () => {
            setTemplatesModified(false);
            handleCloseConfirmation();
          },
          onError: (error) => console.error('Mutation failed:', error),
        }
      );
    }
  };

  const handleFormSubmit = () => {
    if (templatesModified) {
      persist();
    } else {
      handleCloseConfirmation();
    }
  };

  const handleCloseWithCheck = (closeAction: () => void) => {
    if (formDirty) {
      setPendingCloseAction(() => closeAction);
      setShowUnsavedDialog(true);
    } else {
      closeAction();
    }
  };

  const handleConfirmClose = () => {
    setShowUnsavedDialog(false);
    if (pendingCloseAction) {
      pendingCloseAction();
      setPendingCloseAction(null);
    }
  };

  const handleCancelClose = () => {
    setShowUnsavedDialog(false);
    setPendingCloseAction(null);
  };

  const handleAddTemplate = () => {
    setEditingTemplate(null);
    setTemplateEditorOpen(true);
  };

  const handleEditTemplate = (name: string) => {
    setEditingTemplate({ name, template: templates[name] });
    setTemplateEditorOpen(true);
  };

  const handleDeleteTemplate = (name: string) => {
    const newTemplates = { ...templates };
    delete newTemplates[name];
    setTemplates(newTemplates);
    setTemplatesModified(true);
  };

  const handleSaveTemplate = (name: string, template: OutputTemplate) => {
    const newTemplates = { ...templates };
    if (editingTemplate && editingTemplate.name !== name) {
      delete newTemplates[editingTemplate.name];
    }
    newTemplates[name] = template;
    setTemplates(newTemplates);
    setTemplatesModified(true);
  };

  const getTemplateMetaText = (template: OutputTemplate): string => {
    const parts = [template.file_format || 'CSV', `${template.columns?.length || 0} cols`];
    if (template.filters?.length) parts.push(`${template.filters.length} filters`);
    if (template.split_by) parts.push(`split by ${template.split_by}`);
    if (template.naming_config?.elements?.length) parts.push('custom naming');
    return parts.join(' · ');
  };

  if (isLoading || !packager) {
    return <LinearProgress />;
  }

  return (
    <>
      <DialogContentGlynt paddingTop="0" scrollable>
        <FormControlStyled fullWidth>
          <FormLabelStyled>TEMPLATES</FormLabelStyled>
          <Typography variant="body2" sx={{ marginBottom: '8px', color: 'text.secondary' }}>
            Configure the output templates included in each delivery.
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <AddNewButton onClick={handleAddTemplate} data-cy="btn-add-template" />
          </Box>

          {Object.keys(templates).length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 3,
                color: 'text.secondary',
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2">No templates</Typography>
              <Typography variant="caption">Click "Add New" to create a template</Typography>
            </Box>
          ) : (
            <Box>
              {Object.entries(templates).map(([name, template]) => (
                <Box
                  key={name}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1.5,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    mb: 1,
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 1 },
                  }}
                  onClick={() => handleEditTemplate(name)}
                  data-cy={`template-card-${name}`}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                      {name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getTemplateMetaText(template)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip
                      label={template.report_type || 'activity'}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditTemplate(name);
                      }}
                      data-cy={`btn-edit-template-${name}`}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTemplate(name);
                      }}
                      data-cy={`btn-delete-template-${name}`}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </FormControlStyled>
      </DialogContentGlynt>

      <DialogActionsGlynt
        handleSubmit={handleFormSubmit}
        closeModal={() => handleCloseWithCheck(handleCloseConfirmation)}
        cancelCypressId="btn-packager-config-step3-modal-cancel"
        submitCypressId="btn-packager-config-step3-modal-submit"
        clearCypressId="btn-packager-config-step3-modal-clear"
        clearButton={() => handleCloseWithCheck(handlePrevious)}
        clearButtonText="Previous"
        showClearButton={true}
        submitButtonText={simulateMode ? 'Preview' : 'Save & Close'}
        submitLoading={simulateMode ? simulateLoading : updatePackagerMutation.isPending}
        submitDisabled={simulateMode ? simulateLoading : updatePackagerMutation.isPending}
      />

      <PackagerConfigUnsavedChanges
        open={showUnsavedDialog}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
      />

      {packager?.id && (
        <TemplateEditorDialog
          open={templateEditorOpen}
          onClose={() => setTemplateEditorOpen(false)}
          onSave={handleSaveTemplate}
          editingTemplate={editingTemplate}
          existingTemplateNames={Object.keys(templates)}
          dataPoolId={dpid!}
          packagerId={packager.id}
        />
      )}
    </>
  );
};

export default PackagerStep3Templates;
